import React, { useState } from 'react';
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const IMPORT_TYPES = [
  {
    key: 'users',
    label: 'Usuários',
    filename: 'importacao_usuarios_modelo.xlsx',
    description: 'Importe gestores e consultores. As senhas serão geradas automaticamente.',
    columns: ['username', 'first_name', 'last_name', 'email', 'role', 'is_active'],
  },
  {
    key: 'clients',
    label: 'Clientes',
    filename: 'importacao_clientes_modelo.xlsx',
    description: 'Importe clientes com CPF/CNPJ e dados de contato.',
    columns: ['name', 'document', 'contact_name', 'contact_email', 'contact_phone', 'is_active'],
  },
  {
    key: 'projects',
    label: 'Projetos',
    filename: 'importacao_projetos_modelo.xlsx',
    description: 'Importe projetos vinculados a clientes já cadastrados.',
    columns: ['client_name', 'project_name', 'description', 'hourly_rate', 'is_active'],
  },
];

const ImportCard = ({ config }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownload = async () => {
    try {
      const response = await api.get(`/imports/templates/${config.key}/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', config.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Modelo baixado!');
    } catch (err) {
      toast.error('Erro ao baixar modelo.');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Selecione um arquivo .xlsx');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const response = await api.post(`/imports/${config.key}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      if (response.data.success) {
        toast.success(`${response.data.created} registro(s) importado(s) com sucesso!`);
      } else if (response.data.partial) {
        toast.success(`${response.data.created} registro(s) importado(s). Verifique os erros.`);
      } else {
        toast.error('Nenhum registro importado. Verifique os erros.');
      }
      setFile(null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao importar arquivo.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <FileSpreadsheet size={20} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-800">{config.label}</h3>
          <p className="text-sm text-gray-500 mt-1">{config.description}</p>

          <div className="mt-3 flex flex-wrap gap-2">
            {config.columns.map((col) => (
              <span key={col} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-medium">
                {col}
              </span>
            ))}
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors"
            >
              <Download size={16} />
              Baixar modelo
            </button>

            <form onSubmit={handleUpload} className="flex-1 flex flex-col sm:flex-row gap-3">
              <label className="flex-1 inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer">
                <Upload size={16} />
                <span className="truncate">{file ? file.name : 'Selecionar arquivo .xlsx'}</span>
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => {
                    setFile(e.target.files[0]);
                    setResult(null);
                  }}
                />
              </label>
              <button
                type="submit"
                disabled={loading || !file}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {loading ? 'Importando...' : 'Importar'}
              </button>
            </form>
          </div>

          {result && (
            <div className="mt-5 rounded-lg border overflow-hidden">
              <div className={`px-4 py-3 flex items-center gap-2 text-sm font-medium ${
                result.success ? 'bg-green-50 text-green-800 border-green-200' :
                result.partial ? 'bg-yellow-50 text-yellow-800 border-yellow-200' :
                'bg-red-50 text-red-800 border-red-200'
              }`}>
                {result.success ? <CheckCircle2 size={16} /> :
                 result.partial ? <AlertCircle size={16} /> :
                 <AlertCircle size={16} />}
                {result.success
                  ? `${result.created} registro(s) importado(s) com sucesso.`
                  : `${result.created} registro(s) importado(s). ${result.errors.length} erro(s) encontrado(s).`}
              </div>

              {result.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {result.errors.map((err, idx) => (
                        <tr key={idx} className="bg-white">
                          <td className="px-4 py-2 text-gray-500 w-20">Linha {err.row}</td>
                          <td className="px-4 py-2 text-gray-500 w-32">{err.field}</td>
                          <td className="px-4 py-2 text-red-600">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ImportManager = () => {
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-blue-900">Instruções</h3>
        <p className="text-sm text-blue-700 mt-1">
          Baixe o arquivo modelo, preencha os dados e faça o upload. O sistema importará apenas as linhas válidas e reportará os erros encontrados.
        </p>
      </div>

      {IMPORT_TYPES.map((type) => (
        <ImportCard key={type.key} config={type} />
      ))}
    </div>
  );
};

export default ImportManager;
